import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router'
import { AxiosError } from 'axios'
import { LoginError } from '../../services/authService'
import { Icon } from '@/design-system/atoms/Icon'
import { Alert } from '@/design-system/molecules/Alert'
import { Label } from '@/design-system/atoms/Label'
import { Input } from '@/design-system/atoms/Input'
import { Checkbox } from '@/design-system/atoms/Checkbox'
import { Button } from '@/design-system/atoms/Button'
import { useAuth } from '../../context/auth'
import { STORAGE_KEYS } from '../../lib/storageKeys'
import { SERVER_UNAVAILABLE_EVENT } from '../../components/auth/ServerWakeGuard'

export default function SignInForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [isChecked, setIsChecked] = useState(() => {
    if (typeof window === 'undefined') {
      return true
    }

    const storedValue = window.localStorage.getItem(STORAGE_KEYS.REMEMBER_ME)
    if (storedValue === null) {
      return true
    }

    return storedValue === 'true'
  })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Get the intended destination or default to home
  const from = location.state?.from?.pathname || '/'

  // Identifica falhas de rede / 5xx — situações onde o backend está fora
  // do ar (manutenção programada ou incidente). Diferente de 4xx, que
  // são erros de credencial e devem mostrar a mensagem inline.
  const isSystemUnavailableError = (err: unknown): boolean => {
    // O authService embrulha o erro do axios; sem olhar o original, o popup
    // de servidor fora nunca abria a partir de um login que falhou.
    const original = err instanceof LoginError ? err.original : err
    if (!(original instanceof AxiosError)) return false
    if (!original.response) return true // sem resposta = network/timeout
    return original.response.status >= 500
  }

  const submitLogin = async () => {
    setError('')

    if (!email || !password) {
      setError('Por favor, preencha todos os campos')
      return
    }

    setIsLoading(true)

    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      if (isSystemUnavailableError(err)) {
        // Delega o popup ao ServerWakeGuard (montado uma vez no layout),
        // passando o email para o fallback de WhatsApp.
        window.dispatchEvent(
          new CustomEvent(SERVER_UNAVAILABLE_EVENT, {
            detail: { email, source: 'signin' },
          })
        )
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao fazer login')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitLogin()
  }

  const handleRememberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setIsChecked(checked)
    window.localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(checked))
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="space-y-6">
          <div>
            <Label>
              E-mail <span className="text-error-500">*</span>{' '}
            </Label>
            <Input
              type="email"
              placeholder="Digite seu e-mail"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
          <div>
            <Label>
              Senha <span className="text-error-500">*</span>{' '}
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
              >
                {showPassword ? (
                  <Icon
                    name="eye"
                    size={20}
                    className="text-gray-500 dark:text-gray-400"
                  />
                ) : (
                  <Icon
                    name="eye-off"
                    size={20}
                    className="text-gray-500 dark:text-gray-400"
                  />
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox checked={isChecked} onChange={handleRememberChange} />
              <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                Manter conectado
              </span>
            </div>
            <Link
              to="/reset-password"
              className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              Esqueceu a senha?
            </Link>
          </div>
          <div>
            <Button
              type="submit"
              className="w-full"
              size="sm"
              disabled={isLoading}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
